import * as QueryKeys from "data";

import { Box, Card, CardContent, CardHeader, FormGroup } from "@mui/material";
import { useIdentity } from "contexts/identity-context";

import { makeStyles } from "@mui/styles";
import { fetchCustomDatasets, fetchCustomDefinitions } from "data/queries";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { resolveIdentityId } from "utils/identity";
import { customColumns, customersToRows } from "./columns-rows";

import { GridRowClassNameParams } from "@mui/x-data-grid-premium";
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import CustomServerPaginatedTable from "components/shared/server-paginated-table";
import * as localStorageKeys from "data/local-storage-keys";
import { CustomDatasetFilterType, CustomDatasetList } from "data/models";
import usePaginatedTableQuery from "shared-hooks/use-paginated-table-query";
import FilterComponent from "../share-component/filter-component";
import { updateQueryAndFilters } from "../utils";
import { CustomDatasetFilters } from "./filters";

export const CustomDownloadPage = () => {
  const [identityState] = useIdentity();

  const identityId = resolveIdentityId(identityState);

  const savedSearch = localStorage.getItem(
    localStorageKeys.searchCustomDatasets,
  );

  let initialQueryFilterState: CustomDatasetFilterType = {
    name: [],
    query: "",
    product: [],
    release: "",
    latestRelease: false,
    customerName: [],
    notPublished: false,
  };

  const [queryFilterState, setQueryFilterState] =
    useState<CustomDatasetFilterType>(initialQueryFilterState);

  const [loadingFilters, setLoadingFilters] = useState<boolean>(false);

  const { paginationModel, handleChangePaginationModel, searchQuery } =
    usePaginatedTableQuery<CustomDatasetList>(
      (limit: number, offset: number) => {
        return fetchCustomDatasets(queryFilterState, limit, offset);
      },
      QueryKeys.customDatasetsSearchQuery,
      identityId,
      queryFilterState,
    );

  const definitions = useQuery({
    queryKey: [QueryKeys.customDatasetDefinitions],
    queryFn: () => fetchCustomDefinitions(),
  });

  useEffect(() => {
    updateQueryAndFilters({
      savedSearch: savedSearch,
      setQueryFilterState,
      setLoadingFilters,
      initialQueryFilterState,
      definitions: definitions,
    });
  }, []);

  const useStyles = makeStyles({
    highlightedRow: {
      color: "grey",
    },
  });

  const classes = useStyles();

  const getCustomRowLicence = (params: GridRowClassNameParams): string => {
    if (params.row.licenced == false) {
      return classes.highlightedRow;
    }
    return "";
  };

  const customColumns_ = customColumns();

  const isSuccess = searchQuery.isSuccess;
  const isLoading = searchQuery.isPending;
  const rawsData = searchQuery.data ? customersToRows(searchQuery.data) : [];

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          gap: "16px",
          position: "relative",
        }}
      >
        <ReactQueryErrorWrapper
          queries={[searchQuery, definitions]}
          mutations={[]}
        />

        <Card>
          <CardContent
            style={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
            }}
          >
            <FormGroup style={{ display: "flex", flexDirection: "row" }}>
              {loadingFilters ? (
                <Box
                  style={{
                    display: "flex",
                    padding: "0px",
                    gap: "10px",
                  }}
                >
                  <h2>Loading...</h2>
                </Box>
              ) : (
                <FilterComponent
                  queryFilterState={queryFilterState}
                  setQueryFilterState={setQueryFilterState}
                  initialQueryFilterState={initialQueryFilterState}
                  renderFilter={CustomDatasetFilters}
                  definitions={definitions}
                  localStorageKey={localStorageKeys.searchCustomDatasets}
                />
              )}
            </FormGroup>
          </CardContent>
        </Card>

        <CustomServerPaginatedTable
          rowCount={searchQuery.data?.links?.numberMatched ?? -1}
          paginationModel={paginationModel}
          setPaginationModel={handleChangePaginationModel}
          getCustomRowClass={getCustomRowLicence}
          rows={rawsData}
          cols={customColumns_}
          isLoading={isLoading}
          overlayText={
            "We couldn't find any datasets that match your filter criteria."
          }
          disableColumnMenu
          disableColumnSelector
        />
      </div>
    </>
  );
};
